import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './BehaviorInsights.css';

type DateRange = '7days' | '30days' | '90days' | 'year';

interface DailyActiveCount {
  date: string;
  count: number;
}

interface PageEngagement {
  page: string;
  views: number;
  uniqueUsers: number;
}

interface SearchInsight {
  totalSearches: number;
  uniqueSearchers: number;
  topQueries: Array<{ query: string; count: number }>;
  searchToClickRate: number;
}

interface WhyNoBuy {
  activeButNoClick: number;
  activeButNoClickPercent: number;
  viewedProductsButNoClick: number;
  searchedButNoClick: number;
  possibleReasons: string[];
}

interface BehaviorData {
  dailyActiveUsers: DailyActiveCount[];
  pageEngagement: PageEngagement[];
  searchInsight: SearchInsight;
  actionBreakdown: Record<string, number>;
  retention: {
    avgVisitFrequency: number;
    returnRate: number;
    usersWithMultipleVisits: number;
    totalUsersAnalyzed: number;
  };
  productEngagement: {
    totalViews: number;
    totalClicks: number;
    viewToClickRate: number;
    uniqueInteractors: number;
  };
  whyNoBuy: WhyNoBuy;
  summary: {
    totalProfiles: number;
    usersWithActivity: number;
    range: string;
  };
}

export default function BehaviorInsights() {
  const { t } = useTranslation('admin');
  const [dateRange, setDateRange] = useState<DateRange>('30days');
  const [data, setData] = useState<BehaviorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        `${getApiBaseUrl()}/getBehaviorInsights?range=${dateRange}`
      );
      if (response.ok) {
        const text = await response.text();
        if (!text) {
          setError('Empty response from server');
        } else {
          const result = JSON.parse(text);
          if (result.success) {
            setData(result.data);
          } else {
            setError('Failed to load behavior insights');
          }
        }
      } else {
        setError('Failed to load behavior insights');
      }
    } catch (err) {
      console.error('Error loading behavior insights:', err);
      setError('Failed to load behavior insights');
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="behavior-loading">
        <div className="loading-spinner"></div>
        <p>Analyzing behavior patterns...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="behavior-error">
        <p>{error}</p>
        <button onClick={loadData} className="retry-btn">Retry</button>
      </div>
    );
  }

  if (!data) return null;

  // Find max daily active for chart scaling
  const maxDailyActive = Math.max(...data.dailyActiveUsers.map(d => d.count), 1);

  return (
    <div className="behavior-insights">
      {/* Header */}
      <div className="behavior-header">
        <h3>🧠 {t('analytics.behavior.title', 'Behavior Insights')}</h3>
        <div className="date-range-selector">
          {(['7days', '30days', '90days', 'year'] as DateRange[]).map((range) => (
            <button
              key={range}
              className={`range-btn ${dateRange === range ? 'active' : ''}`}
              onClick={() => setDateRange(range)}
            >
              {t(`analytics.dateRange.${range}`, range)}
            </button>
          ))}
        </div>
      </div>

      {/* WHY THEY DON'T BUY — Primary section */}
      <div className="why-no-buy-section">
        <h4>🔍 Why Users Don&apos;t Convert</h4>
        <div className="why-no-buy-grid">
          <div className="why-card critical">
            <div className="why-value">{data.whyNoBuy.activeButNoClickPercent}%</div>
            <div className="why-label">Browse Without Clicking</div>
            <div className="why-detail">
              {data.whyNoBuy.activeButNoClick} of {data.summary.usersWithActivity} active users
            </div>
          </div>
          <div className="why-card warning">
            <div className="why-value">{data.whyNoBuy.searchedButNoClick}</div>
            <div className="why-label">Searched but No Click</div>
            <div className="why-detail">Users who searched for products but didn&apos;t engage</div>
          </div>
          <div className="why-card warning">
            <div className="why-value">{data.whyNoBuy.viewedProductsButNoClick}</div>
            <div className="why-label">Viewed Products, No Click</div>
            <div className="why-detail">Saw product recommendations but didn&apos;t click through</div>
          </div>
        </div>

        {/* Auto-detected reasons */}
        {data.whyNoBuy.possibleReasons.length > 0 && (
          <div className="auto-insights">
            <h5>⚡ Auto-Detected Issues</h5>
            <ul>
              {data.whyNoBuy.possibleReasons.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Retention Metrics */}
      <div className="retention-section">
        <h4>📈 Retention & Engagement</h4>
        <div className="retention-grid">
          <div className="retention-card">
            <div className="retention-value">{data.retention.returnRate}%</div>
            <div className="retention-label">Return Rate</div>
            <div className="retention-desc">Users who came back more than once</div>
          </div>
          <div className="retention-card">
            <div className="retention-value">{data.retention.avgVisitFrequency}</div>
            <div className="retention-label">Avg Visit Days</div>
            <div className="retention-desc">Average active days per user in period</div>
          </div>
          <div className="retention-card">
            <div className="retention-value">{data.productEngagement.viewToClickRate}%</div>
            <div className="retention-label">View → Click Rate</div>
            <div className="retention-desc">Product views that led to affiliate clicks</div>
          </div>
          <div className="retention-card">
            <div className="retention-value">{data.searchInsight.searchToClickRate}%</div>
            <div className="retention-label">Search → Click Rate</div>
            <div className="retention-desc">Searchers who clicked an affiliate link</div>
          </div>
        </div>
      </div>

      {/* Daily Active Users Chart (simple bar chart) */}
      {data.dailyActiveUsers.length > 0 && (
        <div className="dau-section">
          <h4>📊 Daily Active Users</h4>
          <div className="dau-chart">
            {data.dailyActiveUsers.slice(-30).map((day) => (
              <div key={day.date} className="dau-bar-wrapper" title={`${day.date}: ${day.count} users`}>
                {day.count > 0 && (
                  <span className="dau-bar-count">{day.count}</span>
                )}
                <div
                  className="dau-bar"
                  style={{ height: day.count > 0 ? `${Math.max((day.count / maxDailyActive) * 100, 6)}%` : '0%' }}
                />
                <span className="dau-label">
                  {day.date.slice(5)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Insights */}
      <div className="search-section">
        <h4>🔎 Search Behavior</h4>
        <div className="search-stats">
          <div className="search-stat">
            <span className="stat-value">{data.searchInsight.totalSearches}</span>
            <span className="stat-label">Total Searches</span>
          </div>
          <div className="search-stat">
            <span className="stat-value">{data.searchInsight.uniqueSearchers}</span>
            <span className="stat-label">Unique Searchers</span>
          </div>
        </div>
        {data.searchInsight.topQueries.length > 0 && (
          <div className="top-queries">
            <h5>Top Search Queries</h5>
            <div className="query-list">
              {data.searchInsight.topQueries.slice(0, 10).map((q) => (
                <div key={q.query} className="query-row">
                  <span className="query-text">{q.query}</span>
                  <span className="query-count">{q.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Page Engagement */}
      {data.pageEngagement.length > 0 && (
        <div className="page-engagement-section">
          <h4>📄 Page Engagement</h4>
          <div className="engagement-table">
            <div className="engagement-header-row">
              <span>Page</span>
              <span>Views</span>
              <span>Users</span>
            </div>
            {data.pageEngagement.slice(0, 10).map((page) => (
              <div key={page.page} className="engagement-row">
                <span className="engagement-page">{page.page}</span>
                <span className="engagement-views">{page.views}</span>
                <span className="engagement-users">{page.uniqueUsers}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Actions */}
      {Object.keys(data.actionBreakdown).length > 0 && (
        <div className="actions-section">
          <h4>⚡ User Actions</h4>
          <div className="actions-grid">
            {Object.entries(data.actionBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([action, count]) => (
                <div key={action} className="action-chip">
                  <span className="action-name">{action.replace(/-/g, ' ')}</span>
                  <span className="action-count">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
